// The initiative strip (slice 12; design/16 §2, §4, §5; design/07 §4): the
// bottom bar's resident. Chevron-shaped stones flowing right to left, the
// leftmost the active turn, the next five after it, the End of Round stone
// where the loop closes — the one place every timed effect ticks. Each stone
// carries the initiative, the name, up to four effect icons by the DM's
// priority list (empty without one, a "+n" when truncated), identity colour
// on its edge and a small mark when that person is disconnected. Clicking a
// stone focuses its creature on the board. When nothing is running the bar
// hosts Start Encounter, so it always earns its space.
//
// One order, seen redacted: the stones come from this viewer's reading — a
// player's holds no stone for a creature they have not been shown, and sits
// still for a beat while something unseen acts. Back is not undo, said here.
//
// The site's look (issue 1541): the round in gold Cinzel over End turn (gold), Back and End; the stones on raised with a
// 4 px edge in their owner's colour, the initiative bold over the name, effects at the top right; the active stone gold
// with dark words; END OF ROUND closes the loop. What is on the strip, and when, is unchanged.
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  betweenTurnsAsks, combatantSeen, dmEncounterView, effectBadge, emptyEncounter, stoneEffects, stripStones,
  endTurn as endTurnOf, type Encounter, type PlayerCombatant, type PlayerEncounter, type TableMember, type TableObject,
  memberLabel, tallyActions, type ActionRules, type ActionSpend, passScore,
  floorOf,
} from '@dragons-table/vtt-shared';
import { boardBus } from '../../board/bus';
import { useTableState } from '../../table/TableState';
import { useMayAct, useViewOnlyFor } from '../../table/mayAct';
import { VIEW_ONLY_REASON } from '../../table/viewOnly';
import { useNumberWords } from '../../table/numberWords';
import { ConfirmDialog } from '../Dialogs';
import { useShell } from '../context';
import { useStartEncounter } from './startEncounter';
import { InitiativePrompt } from './InitiativePrompt';
import { stripHeight } from '../stripHeight';
import { roundWords } from './PhaseList';
import { coarsePointer } from '../device';
import { BTN, PRIMARY, PRIMARY_SM } from '../ui';
import { DelayControls } from './DelayParts';

/** Said when the DM steps back, not only on hover, so a touch screen sees it too (design/16 §5; issue 1213). */
export const BACK_NOTICE = 'Back moves the turn only: an effect that ended stays ended, and no token moves.';

const btn = BTN;

/** The person whose identity colour a stone wears: a player who owns a token acting on it (the DM's own creatures wear none). */
export function stoneOwner(c: PlayerCombatant, objects: Record<string, { owners?: string[] }>, members: TableMember[]): TableMember | null {
  for (const id of c.tokens) {
    const o = objects[id];
    for (const owner of o?.owners ?? []) {
      const m = members.find((x) => x.id === owner && x.role === 'player');
      if (m) return m;
    }
  }
  return null;
}

/**
 * The DM's quiet note on Start Encounter (design/20 Q1): each seated player with no token in the fight, and what is true
 * of them (qa-runs 1256): no token on the board at all, or one that stands outside the fight, usually because it was
 * never linked to their character.
 */
export function startNotes(enc: Encounter, objects: Record<string, TableObject>, members: TableMember[]): string[] {
  const inFight = new Set(enc.combatants.flatMap((c) => c.tokens));
  const tokens = Object.values(objects).filter((o) => o.kind === 'token');
  return members.filter((m) => m.role === 'player' && !tokens.some((o) => o.owners.includes(m.id) && inFight.has(o.id))).map((m) => {
    const who = memberLabel(m, objects); // by character and handle (issue 1023)
    const theirs = tokens.filter((o) => o.owners.includes(m.id));
    if (!theirs.length) return `${who} has no token on the board.`;
    const plain = m.characters.length > 0 ? theirs.find((o) => !o.character && !o.entry) : undefined;
    if (plain) return `${plain.name} is on the board but outside this fight, and isn't linked to ${who}'s character: link it in Properties, then add it to the fight.`;
    return `${theirs[0]!.name} is on the board but outside this fight: select it and add it to the fight for ${who} to take a turn.`;
  });
}

function EffectDots({ c, priority }: { c: PlayerCombatant; priority: string[] }) {
  const { shown, more } = stoneEffects(c.effects ?? [], priority);
  if (shown.length === 0 && more === 0) return null;
  return (
    <span className="flex items-center gap-0.5" data-testid="stone-effects">
      {shown.map((e) => (
        <span key={e.id} title={effectBadge(e)} aria-label={effectBadge(e)} role="img"
          className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[0.5625rem] font-bold leading-none text-stone-950 ${e.good ? 'bg-green-400' : 'bg-red-400'}`}>
          {e.label.slice(0, 1).toUpperCase()}
        </span>
      ))}
      {more > 0 && <span className="text-[0.625rem] text-stone-400" title={`${more} more`}>+{more}</span>}
    </span>
  );
}

/**
 * The turn's actions (issue 1131): the active combatant's row of the system's kinds, each with what this turn used and
 * what is left, a tap marking one and a small − taking the last back. Shown to its owner and the DM only; over is
 * flagged only when the server flagged it (the campaign's rule-check level). Never refused: going over is the player's.
 */
/** A kind's short name on a touch screen: "Standard action" reads "Std", so the six fit one row on a tablet. */
const SHORT: Record<string, string> = { standard: 'Std', full_round: 'Full', immediate: 'Imm' };
const shortLabel = (key: string, label: string): string => SHORT[key] ?? label.replace(/ action$/i, '');

function TurnActionsRow({ rule, combatant, spent, onMark }: { rule: ActionRules; combatant: string; spent: ActionSpend[]; onMark: (action: string, spend: boolean) => void }) {
  const rows = tallyActions(rule, spent);
  const over = rows.filter((r) => r.over > 0);
  // Under a finger the row stays one line (a tablet's board is short already): short names, and it scrolls sideways
  // rather than wrapping if a narrow screen still cannot fit it. The full name is the button's name and title.
  const compact = coarsePointer();
  return (
    <div className={`flex items-center gap-1 text-xs ${compact ? 'flex-nowrap overflow-x-auto' : 'flex-wrap'}`} data-testid="turn-actions" data-combatant={combatant}>
      {rows.map((r) => (
        <span key={r.key} className="flex shrink-0 items-stretch">
          <button type="button" data-testid={`action-${r.key}`} data-kind={r.key} data-over={r.over > 0 ? 'true' : 'false'}
            className={`rounded-l border ${compact ? 'whitespace-nowrap px-1.5' : 'px-2'} py-0.5 text-left ${r.over > 0 ? 'border-red-500 text-red-300' : r.used > 0 ? 'border-amber-500 text-amber-200' : 'border-stone-600 text-stone-300'} hover:bg-stone-800 ${r.used > 0 ? '' : 'rounded-r'}`}
            title={`Mark a ${r.label} taken this turn`}
            aria-label={compact ? `${r.label}: ${r.used} used, ${r.left === null ? 'any' : `${r.left}`} left` : undefined}
            onClick={() => onMark(r.key, true)}>
            <span className="font-medium">{compact ? shortLabel(r.key, r.label) : r.label}</span>
            <span className="ml-1 tabular-nums text-stone-400">{r.used} used · {r.left === null ? 'any' : `${r.left} left`}</span>
          </button>
          {r.used > 0 && (
            <button type="button" aria-label={`Take back a ${r.label}`} title={`Take back a ${r.label}`}
              className="rounded-r border border-l-0 border-stone-600 px-1.5 text-stone-300 hover:bg-stone-800"
              onClick={() => onMark(r.key, false)}>−</button>
          )}
        </span>
      ))}
      {over.length > 0 && (
        <span className={`text-red-300 ${compact ? 'shrink-0 whitespace-nowrap' : ''}`} role="status" data-testid="turn-actions-over">Over what a turn allows: {over.map((r) => r.label).join(', ')}.</span>
      )}
    </div>
  );
}

/**
 * End turn, the strip's and the Turn tracker's (qa-runs 160): the DM ending a player's turn asks first (design/16 §5), so
 * it cannot happen on a stray click; the DM's own creatures end at once. `dialog` goes wherever the button is drawn.
 */
export function useEndTurn(raw: Encounter | PlayerEncounter): { request: () => void; dialog: JSX.Element | null } {
  const { table } = useShell();
  const { store, role, act } = useTableState();
  const mayAct = useMayAct();
  const runs = role === 'dm' && mayAct('encounter.next');
  const [confirming, setConfirming] = useState<string | null>(null);
  const end = () => { const b = endTurnOf(raw); if (b) act(b); };
  const request = () => {
    const objects = store.state.objects as Record<string, TableObject>;
    const reading: PlayerEncounter = role === 'dm' ? dmEncounterView(raw as Encounter) : (raw as PlayerEncounter);
    const active = reading.active ? reading.combatants.find((c) => c.id === reading.active) ?? null : null;
    const owner = active ? stoneOwner(active, objects, table.members) : null;
    if (runs && owner) { setConfirming(memberLabel(owner, objects, { about: active?.tokens.map((id) => objects[id]).find((t) => t?.owners?.includes(owner.id)) })); return; }
    end();
  };
  const dialog = confirming ? (
    <ConfirmDialog title={`End ${confirming}'s turn?`} confirmLabel="End their turn" cancelLabel="Cancel"
      body={<p>The turn moves on and they can no longer move until their next one. They can end their own turn themselves.</p>}
      onConfirm={() => { end(); setConfirming(null); }} onClose={() => setConfirming(null)} />
  ) : null;
  return { request, dialog };
}

/**
 * The DM's prompt between turns (dnd-5e.md §5.5): *Legendary actions, Dragon? (2 left)*, a button per cost and Skip. It
 * never holds up the next turn: nothing waits on it, and the prompt is gone when the turn moves on. The action itself
 * is the DM's, from the token's bar. Shown on the strip and in the Turn tracker alike (qa-runs#1454: a DM ending turns
 * from the strip never saw it).
 */
export function BetweenTurns({ enc, objects }: { enc: Encounter; objects: Record<string, TableObject> }) {
  const { act, setNotice, systemRules } = useTableState();
  const [closed, setClosed] = useState<string[]>([]);
  const gap = `${enc.round}:${enc.activeIndex}`;
  const asks = betweenTurnsAsks(enc, systemRules.pools).filter((a) => !closed.includes(`${gap}:${a.combatant}`) && !enc.combatants.find((c) => c.id === a.combatant)?.tokens.every((id) => objects[id]?.dead));
  if (!asks.length) return null;
  const close = (id: string) => setClosed((was) => [...was.filter((x) => x.startsWith(`${gap}:`)), `${gap}:${id}`]);
  return (
    <div className="space-y-0.5 text-xs text-amber-200" data-testid="between-turns">
      {asks.map((a) => (
        <div key={`${a.combatant}:${a.key}`} className="flex flex-wrap items-center gap-1">
          <span>{a.name}: {a.label}? ({a.left} left)</span>
          {Array.from({ length: a.left }, (_, i) => i + 1).map((n) => (
            <button key={n} type="button" className={BTN} onClick={() => { close(a.combatant); act({ kind: 'encounter.pool', id: a.combatant, key: a.key, max: a.max, left: a.left - n }, (r) => { if (!r.ok) setNotice(r.reason ?? 'That could not be spent.'); }); }}>Use {n}</button>
          ))}
          <button type="button" className={BTN} onClick={() => close(a.combatant)}>Skip</button>
        </div>
      ))}
    </div>
  );
}

export function InitiativeStrip() {
  const { table, phone = false } = useShell();
  // The phone layout's strip (issue 1242): Back and End Encounter behind ⋯, so the turn's own controls keep one row.
  const [more, setMore] = useState(false);
  const { store, role, act, setNotice, systemRules } = useTableState();
  const words = useNumberWords();
  const isDm = role === 'dm';
  // The DM's controls need `tokens` (issues 1159, 1160); the DM's reading of the order follows the board alone.
  const mayAct = useMayAct();
  const runs = isDm && mayAct('encounter.next');
  // At a view-only table (issue 1583) the server refuses a player's End my turn, so it is greyed with the reason.
  const endLocked = useViewOnlyFor()('encounter.next');
  // Sized to its content (issue 1070): the strip reports its natural height and the dock fits the bottom bar to it.
  // The root is never h-full, so what is measured is the content: one line idle, the header and the stones running.
  const root = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    stripHeight.report(el.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => stripHeight.report(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  });
  const raw = (store.state.encounter ?? emptyEncounter()) as Encounter | PlayerEncounter;
  const reading: PlayerEncounter = isDm ? dmEncounterView(raw as Encounter) : (raw as PlayerEncounter);
  // The active turn and the next five (design/16 §2); "+N more" shows the whole order (issue 1552), so a combatant later in
  // it — a late joiner's own character — is never off the strip with nothing to say so.
  const [all, setAll] = useState(false);
  const stones = useMemo(() => stripStones(reading, all ? reading.combatants.length * Math.max(1, reading.passes?.phases.length ?? 1) : undefined), [reading, all]);
  const onStrip = new Set(stones.flatMap((s) => (s.kind === 'combatant' ? [s.combatant.id] : [])));
  const offStrip = reading.combatants.filter((c) => !onStrip.has(c.id));
  const objects = store.state.objects as Record<string, TableObject>;
  const active = reading.active ? reading.combatants.find((c) => c.id === reading.active) ?? null : null;
  const hiddenFromPlayers = (id: string): boolean => {
    if (!isDm) return false;
    const c = (raw as Encounter).combatants.find((x) => x.id === id);
    return c ? !combatantSeen(c, store.state as { objects: Record<string, TableObject> }, { id: '', role: 'player' }) : false;
  };

  // Start sorts first and names the unrolled (issue 1021): shared with the tracker's button.
  const { request: start, prompt: startPrompt } = useStartEncounter(raw as Encounter, objects, table.members);
  const { request: endTurn, dialog: endTurnDialog } = useEndTurn(raw);

  // The stones run past a phone's width (issue 1242): the turn's own stays in sight, a swipe shows the rest.
  useLayoutEffect(() => {
    if (!phone) return;
    const stone = root.current?.querySelector<HTMLElement>('[data-testid="stones"] [aria-current="true"]');
    const row = stone?.closest('ol');
    if (stone && row) row.scrollLeft += stone.getBoundingClientRect().left - row.getBoundingClientRect().left - 8;
  }, [phone, reading.active, reading.round]);

  if (!reading.running) {
    return (
      // Idle, one line (issue 1070): the button and a note cut to the bar's width, the whole note in its title.
      <div ref={root} className={`flex min-w-0 ${phone ? 'flex-nowrap' : 'flex-wrap'} items-center gap-3 border-t border-vtt-gold-dim px-3 py-1.5 text-sm text-stone-400`} data-testid="initiative-strip" data-running="false">
        {runs ? (
          <>
            <button type="button" className={`${PRIMARY} ${phone ? 'shrink-0 whitespace-nowrap' : ''}`} onClick={start}
              disabled={(raw as Encounter).combatants.length === 0} title={(raw as Encounter).combatants.length === 0 ? 'Add combatants in the Turn tracker first' : 'Sort by initiative and turn the order on at the highest'}>
              Start Encounter
            </button>
            {(() => {
              const note = (raw as Encounter).combatants.length === 0 ? 'Nothing is in the fight yet — the Turn tracker (Combat menu) adds combatants, or select tokens and add them to the encounter.' : `${(raw as Encounter).combatants.length} in the order. The active turn and the next five will show here.`;
              return <span className="min-w-0 truncate" title={note}>{note}</span>;
            })()}
            {startPrompt}
          </>
        ) : <span>No encounter is running.</span>}
        {/* The DM's call for initiative (qa-runs 405): this player's own prompt, before the fight or after it starts. */}
        {!runs && <InitiativePrompt />}
      </div>
    );
  }

  // The chevron (design/16 §2): the first stone has no notch, every other one takes the point of the one before.
  const CHEVRON = 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)';
  const CHEVRON_FIRST = 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)';
  const quiet = 'flex items-center justify-center whitespace-nowrap bg-vtt-sunk py-1 pl-6 pr-5 text-xs text-stone-300 hover:bg-stone-800';
  return (
    // The site's look (issue 1541): the bar's card with a gold-dim top edge; the round in gold Cinzel with the turn's
    // buttons under it on the left (End turn the gold one), the stones on raised to its right, the active one gold.
    <div ref={root} className={`flex flex-col gap-1 border-t border-vtt-gold-dim ${phone ? 'px-2 py-1' : 'px-3 py-2'}`} data-testid="initiative-strip" data-running="true" data-round={reading.round}>
      {!runs && <InitiativePrompt />}
      {/* The DM's prompt between turns (qa-runs#1454), above the controls: only a running fight has one. */}
      {runs && <BetweenTurns enc={raw as Encounter} objects={objects} />}
      {/* In the phone layout (issue 1242) the controls are one row, the stones under it or, turned sideways, beside it. */}
      <div className={phone ? 'flex min-w-0 flex-col gap-1 landscape:flex-row landscape:items-stretch landscape:gap-2' : 'flex min-w-0 flex-nowrap items-center gap-4'}>
        <div className={phone ? 'flex min-w-0 shrink-0 flex-row flex-wrap items-center gap-1.5' : 'flex min-w-[7.5rem] max-w-[40%] shrink-0 flex-col gap-1.5'} data-testid="strip-controls">
          <span className="flex items-baseline gap-1">
            <span data-testid="strip-round" className="vtt-title text-[0.9375rem] font-bold tracking-[0.06em]">{roundWords(reading, systemRules.units[0]?.label)}</span>
            {reading.active === null && <span className="text-stone-400" data-testid="strip-paused" title="A turn you cannot see is under way.">·</span>}
          </span>
          <span className={`flex flex-wrap ${phone ? 'items-center' : ''} gap-1.5`}>
            {!phone && active?.mine && !runs && active.name && <span className="self-center text-xs text-vtt-gold" data-testid="strip-my-token">Your turn: {active.name}</span>}
            {active?.mine && !runs && <button type="button" className={PRIMARY_SM} disabled={endLocked} title={endLocked ? VIEW_ONLY_REASON : undefined}
              onClick={() => { const b = endTurnOf(raw); if (b) act(b); }}>{reading.passes ? 'End my phase' : 'End my turn'}</button>}
            {runs && (
              <>
                <button type="button" className={PRIMARY_SM} onClick={endTurn}>{reading.passes ? 'End phase' : 'End turn'}</button>
                {/* The turn is a combatant's with no token on the board (issue 1537: one whose token was deleted kept taking
                    turns): it can leave the order from here, the pointer staying on its slot as the reducer keeps it. */}
                {(!phone || more) && active && !active.tokens.some((id) => objects[id]) && (
                  <button type="button" className={btn} aria-label={`Remove ${active.name ?? 'this combatant'} from the turn order`} title="It has no token on the board."
                    data-testid="strip-remove-tokenless" onClick={() => act({ kind: 'encounter.remove', ids: [active.id] })}>Remove from turn order</button>
                )}
                {(!phone || more) && <button type="button" className={btn} title={BACK_NOTICE} onClick={() => act({ kind: 'encounter.back' }, (r) => { if (r.ok) setNotice(BACK_NOTICE); })}>Back</button>}
                {(!phone || more) && <button type="button" className={btn} onClick={() => act({ kind: 'encounter.end' })}>End Encounter</button>}
              </>
            )}
            {phone && <button type="button" className={btn} aria-expanded={more} aria-label={more ? 'Fewer turn controls' : 'More turn controls'} data-testid="strip-more" onClick={() => setMore((v) => !v)}>⋯</button>}
            {/* Delay and Act now (qa-runs#1171): only where the table's system has a delayed action. */}
            {(!phone || more) && <DelayControls compact />}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {(!phone || more) && active && (runs || active.mine) && systemRules.actions && (
            <TurnActionsRow rule={systemRules.actions} combatant={active.id}
              spent={[...(reading.turn && reading.turn.combatant === active.id ? reading.turn.spent : []), ...(reading.roundSpent?.[active.id] ?? [])]}
              onMark={(action, spend) => act({ kind: 'encounter.action', combatant: active.id, action, spend })} />
          )}
          {/* A character down (docs/MRCS/dnd-5e.md §5.5): its marks, and its save on its turn unless it is stable. */}
          {active && (runs || active.mine) && systemRules.deathMarks && active.tokens.map((id) => objects[id]).filter((o) => o?.values.dying).map((o) => (
            <div key={o!.id} className="flex items-center gap-2 text-xs" data-testid="death-saves">
              <span>{`${o!.values.dying!.successes}/${systemRules.deathMarks!.successes} successes, ${o!.values.dying!.failures}/${systemRules.deathMarks!.failures} failures`}</span>
              {!o!.values.dying!.stable && !o!.dead && <button type="button" className={btn} data-testid="death-save" onClick={() => act({ kind: 'object.dying', id: o!.id })}>Death save</button>}
            </div>
          ))}
          {/* What lasts a round (docs/MRCS/dnd-5e.md §5.5): each of one's own combatants shows it, and spends it on anyone's turn. */}
          {(!phone || more) && (systemRules.actions?.kinds.some((k) => k.per === 'round') ?? false) && (
            <div className="flex flex-wrap items-center gap-1 text-xs" data-testid="round-actions">
              {reading.combatants.filter((c) => c.id !== active?.id && (runs || (c as { mine?: boolean }).mine)).flatMap((c) => systemRules.actions!.kinds.filter((k) => k.per === 'round').map((k) => {
                const used = reading.roundSpent?.[c.id]?.some((s) => s.kind === k.key) ?? false;
                return <button key={`${c.id}-${k.key}`} type="button" className={btn} aria-pressed={used} data-testid={`round-action-${k.key}`} onClick={() => act({ kind: 'encounter.action', combatant: c.id, action: k.key, spend: !used })}>{`${(c as { name?: string }).name ?? ''} ${k.label}${used ? ' spent' : ''}`}</button>;
              }))}
            </div>
          )}
          <ol className={`flex min-h-0 flex-1 items-stretch gap-1 overflow-x-auto ${phone ? '[scrollbar-width:none]' : ''}`} aria-label="Turn order" data-testid="stones">
            {stones.map((s, k) => {
              if (s.kind === 'end') {
                return (
                  <li key={`end-${k}`} className="flex min-w-[6.875rem] shrink-0 items-center justify-center bg-vtt-sunk px-5 font-display text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-stone-400" style={{ clipPath: CHEVRON }} data-testid="stone-end" title="Where every timed effect loses a round.">
                    End of Round {s.round}
                  </li>
                );
              }
              const c = s.combatant;
              // By passes (issue 1525) a stone is a phase: its score in that pass, when this viewer is shown the initiative.
              const shownScore = s.pass && reading.passes ? passScore({ initRoll: c.initRoll ?? null, scoreModifier: c.scoreModifier ?? 0, woundModifier: c.woundModifier ?? 0 }, s.pass, reading.passes.step) : (typeof c.initRoll === 'number' ? c.initRoll : null);
              const owner = stoneOwner(c, objects, table.members);
              const dim = hiddenFromPlayers(c.id);
              // Every token of it this person holds marked dead (the 2026-10-15 session pass): struck through, so the DM
              // sees at a glance which turns are only to be ended. Its turn is not skipped: that stays the DM's call.
              const held = c.tokens.map((id) => objects[id] as { dead?: boolean } | undefined).filter((t): t is { dead?: boolean } => !!t);
              const dead = held.length > 0 && held.every((t) => t.dead === true);
              // Its floor (qa-runs#1160; design/32 §5) when the scene has more than one: of its first token this person holds.
              const fs = store.state.scene.floors ?? [];
              const first = c.tokens.map((id) => objects[id]).find((t) => !!t) as { floor?: string } | undefined;
              const floorName = fs.length > 1 && first ? fs.find((f) => f.id === floorOf(first, fs))?.name ?? null : null;
              return (
                <li key={s.pass ? `${c.id}-${s.pass}` : c.id} className="flex shrink-0 items-stretch">
                  <button type="button" data-testid={`stone-${c.id}`} data-name={c.name} data-pass={s.pass} aria-current={s.active ? 'true' : undefined} data-dead={dead ? 'true' : undefined}
                    title={`${c.name ?? 'Unnamed'}${dead ? ' — dead' : ''}${owner ? ` — ${memberLabel(owner, objects)}${owner.present ? '' : ', disconnected'}` : ''}${dim ? ' — hidden from the players' : ''}. Click to find it on the board.`}
                    className={`relative flex min-h-[3.75rem] flex-col justify-start py-2 pl-6 pr-7 text-left ${s.active ? 'vtt-stone-active' : 'bg-stone-800 text-stone-100 hover:bg-stone-700'} ${dim ? 'opacity-50' : ''}`}
                    // The width floor inline: a bar's `.vtt-tool .flex > *` lets its children shrink to nothing (index.css).
                    style={{ clipPath: k === 0 ? CHEVRON_FIRST : CHEVRON, minWidth: '9.375rem' }}
                    onClick={() => { const t = c.tokens.find((id) => objects[id]); if (t) boardBus.focus(t); }}>
                    {/* The owner's colour, 4 px, inside the notch (issue 1541): a border would be cut by the chevron. */}
                    {owner?.colour && <span aria-hidden="true" data-testid="stone-edge" className={`absolute inset-y-0 w-1 ${k === 0 ? 'left-0' : 'left-2.5'}`} style={{ backgroundColor: owner.colour }} />}
                    <span className="text-[1.0625rem] font-bold leading-5 tabular-nums" data-testid="stone-init" title={s.pass ? `${words.initiative}, pass ${s.pass}` : words.initiative}>{shownScore ?? '—'}</span>
                    <span className={`flex items-baseline gap-1 whitespace-nowrap text-xs ${s.active ? '' : 'text-stone-300'}`}>
                      <span className={dead ? 'line-through opacity-70' : ''}>{c.name}</span>
                      {owner && !owner.present && <span className="text-[0.625rem] opacity-70" title="disconnected" aria-label="disconnected" role="img">⌁</span>}
                    </span>
                    {floorName && <span className="w-fit rounded bg-stone-900/60 px-1 text-[0.625rem] text-stone-300" data-testid="stone-floor" title="Its floor">{floorName}</span>}
                    <span className="absolute right-6 top-2"><EffectDots c={c} priority={reading.priority} /></span>
                  </button>
                </li>
              );
            })}
            {offStrip.length > 0 && (
              <li className="flex shrink-0 items-stretch">
                <button type="button" data-testid="stone-more" onClick={() => setAll(true)} title={`Also in the order: ${offStrip.map((c) => c.name ?? 'Unnamed').join(', ')}. Show the whole order.`}
                  className={quiet} style={{ clipPath: CHEVRON }}>
                  +{offStrip.length} more
                </button>
              </li>
            )}
            {all && (
              <li className="flex shrink-0 items-stretch">
                <button type="button" data-testid="stone-fewer" onClick={() => setAll(false)} title="Back to the active turn and the next five."
                  className={quiet} style={{ clipPath: CHEVRON }}>
                  Fewer
                </button>
              </li>
            )}
          </ol>
        </div>
      </div>
      {endTurnDialog}
    </div>
  );
}
